import React, { ReactNode, createContext, useContext } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { useFullTheme, IconType } from '~/design';
import { useCurrentBreakpoint, useResponsive } from '~/utils';
import { MultivariantIconType } from '~/design/types';
import { IconWrapper, useLinkAs } from '~/internal';
import { Responsive, down } from '~/design/breakpoint';

type Orientation = 'horizontal' | 'vertical';

export interface TabsProps {
  /** The alignment of the tabs. Only applies when orientation='horizontal'. (default: left) */
  align?: 'left' | 'center' | 'right';

  /** When aria-labelled by is not present, aria-label should describe the intent of the collection tabs.
   * For example 'School information' or 'Application notes'.  */
  'aria-label'?: string;

  /** Should be used to identify the label or text describing the the Tabs control.  */
  'aria-labelledby'?: string;

  /** The child Tabs components of the component */
  children: React.ReactNode;

  /** The emphasis of the selected tab against its background. (default: highlighted) */
  emphasis?: 'highlighted' | 'plain';

  /** A callback function that is invoked when the tab changes.  */
  onChange?: (value: string) => void;

  /** The orientation of the tabs. (default: horizontal) */
  orientation?: Orientation | Responsive<Orientation>;

  /** The currently selected tab.  */
  value?: string;
}

export interface TabItemProps {
  /** aria-label of the tab, required if no label is present */
  'aria-label'?: string;

  /** Content of the  tab */
  children: React.ReactNode;

  /** Optional icon of the tab */
  icon?: IconType | MultivariantIconType;

  /** Label of the tab */
  label?: ReactNode;

  /** The orientation of the tab's content (default: horizontal) */
  orientation?: Orientation;

  /** The unique identifier of the tab */
  value: string;

  /**
   * If clicking the tab should change the url, then pass this prop to specify
   * which url.  `href` will cause an anchor tag to be rendered instead of a
   * `button`, which is more user friendly.
   */
  href?: string;
}

const TabsContext = createContext(false);

/**
 * Tabs are a go-to method to categorize content on a page, without overwhelming users with too
 * much information at once. Use Tabs to alternate between views within the same context and
 * logically chunk the content behind the tabs so users can easily predict what they'll find
 * when they select a given tab.
 *
 * ## ⚠️ Warnings ⚠️
 * - This component is not yet fully responsive. Using it on `xs` and `sm` screen sizes may lead
 * to a poor user experience.
 */
export function Tabs({
  align = 'left',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  emphasis = 'highlighted',
  onChange,
  orientation: _orientation = 'horizontal',
  value,
}: TabsProps) {
  const responsive = useResponsive();
  const tabIds = (
    React.Children.map(
      children,
      tab => (tab as unknown as React.ReactElement<TabItemProps>)?.props.value ?? '',
    ) as string[]
  ).filter(Boolean);
  const tabSet = new Set(tabIds);
  const currentBreakpoint = useCurrentBreakpoint();
  const orientation = React.useMemo(() => {
    if (typeof _orientation === 'string') {
      return _orientation;
    }
    if (!currentBreakpoint) {
      return 'horizontal';
    }
    if (_orientation[currentBreakpoint]) {
      return _orientation[currentBreakpoint];
    }

    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
    const index = breakpoints.findIndex(bp => bp === currentBreakpoint);
    for (let i = breakpoints.length - 1; i >= 0; i--) {
      const bp = breakpoints[i];
      if (_orientation[bp] && i <= index) {
        return _orientation[bp];
      }
    }

    return 'horizontal';
  }, [currentBreakpoint, _orientation]);

  if (tabSet.size < tabIds.length) {
    throw new Error(
      `Tabs.Item values must be unique within Tabs component.  Please change duplicated Tabs.Item values`,
    );
  }

  if (ariaLabel && ariaLabelledBy) {
    throw new Error(
      'aria-label and aria-labelledby cannot both be used used,  please choose the appropriate aria label',
    );
  }

  const theme = useFullTheme();
  const triggers: React.ReactElement<TabsPrimitive.TabsTriggerProps>[] = [];
  const contents: React.ReactElement<TabsPrimitive.TabsContentProps>[] = [];
  const LinkAs = useLinkAs();

  const emphasisStyle = {
    highlighted: {
      '&[data-state="active"]': {
        backgroundColor: theme.colors.bgPrimaryHighlightHover,
        color: theme.colors.textPrimaryStark,
      },
    },
    plain: {
      backgroundColor: 'transparent',
      color: theme.colors.textSecondaryStark,
      '&:hover': {
        color: theme.colors.textSecondaryStarkHover,
      },
      '&:active': {
        color: theme.colors.textSecondaryStarkPressed,
      },
    },
  }[emphasis];

  React.Children.forEach(children, (tab, index) => {
    if (!tab) {
      return;
    }

    const props = (tab as unknown as React.ReactElement<TabItemProps>).props;
    // Not sure why the eslint-disable-line is necessary, because it's not causing a TSC error...
    const { 'aria-label': ariaLabel, label, icon: TabIcon, value: tabValue } = props; // eslint-disable-line react/prop-types
    const isSelected = value === tabValue;

    if (!label && !ariaLabel) {
      throw new Error(
        `Tabs.Item without a label must have an aria-label. Please add aria-label to ${tabValue}`,
      );
    }

    contents.push(
      <TabsPrimitive.TabsContent
        css={responsive.map(orientation, prop =>
          prop === 'vertical'
            ? {
                flexGrow: 1,
              }
            : {},
        )}
        key={index}
        value={tabValue}
      >
        {tab}
      </TabsPrimitive.TabsContent>,
    );

    const activeColor = theme.colors.actionPrimary;
    const borderRadius = theme.borderRadiuses.control;

    const triggerChildren = (
      <>
        {!!TabIcon && (
          <IconWrapper intent={isSelected ? 'primary' : 'secondary'} selector="button">
            <TabIcon size="md" variant={isSelected ? 'fill' : 'outline'} />
          </IconWrapper>
        )}

        {label && (
          <div
            css={{
              ...responsive.css(theme.typography.labelM),
              alignSelf: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </div>
        )}
      </>
    );
    triggers.push(
      <TabsPrimitive.TabsTrigger
        asChild
        aria-label={ariaLabel}
        css={[
          {
            alignItems: 'center',
            backgroundColor: theme.colors.bgRegular,
            border: 0,
            borderTopLeftRadius: borderRadius,
            color: theme.colors.textSecondary,
            cursor: 'pointer',
            display: 'inline-flex',
            gap: theme.spacing[4],
            minHeight: theme.spacing[12],
            paddingInline: label ? theme.spacing[4] : theme.spacing[3],
            textDecoration: 'none',
            '&[data-orientation="horizontal"]': {
              borderTopRightRadius: borderRadius,
            },
            '&[data-orientation="vertical"]': {
              borderBottomLeftRadius: borderRadius,
            },
            ':hover': {
              backgroundColor: theme.colors.bgRegularHover,
              color: theme.colors.textSecondaryHover,
            },
            '&[data-state="active"]': {
              '&[data-orientation="vertical"]': {
                boxShadow: `inset -4px 0px 0px 0px ${activeColor}`,
              },
              '&[data-orientation="horizontal"]': {
                boxShadow: `inset 0px -4px 0px 0px ${activeColor}`,
              },
              ':hover': {
                backgroundColor: theme.colors.bgPrimaryHover,
                color: theme.colors.textPrimaryStarkHover,
              },
              ':active': {
                backgroundColor: theme.colors.bgPrimaryPressed,
                color: theme.colors.textPrimaryStarkPressed,
              },
            },
            ':focus': {
              outlineColor: activeColor,
              outlineOffset: '-2px',
              outlineStyle: 'solid',
              outlineWidth: '2px',
            },
          },
          emphasisStyle,
        ]}
        key={index}
        value={tabValue}
      >
        {props.href ? (
          <LinkAs href={props.href}>{triggerChildren}</LinkAs>
        ) : (
          <button type="button">{triggerChildren}</button>
        )}
      </TabsPrimitive.TabsTrigger>,
    );
  });

  const border = theme.borders.regular;
  const tabListBorderStyle = `${border.borderColor} ${border.borderWidth}px ${border.borderStyle}`;

  return (
    <TabsContext.Provider value={true}>
      <TabsPrimitive.Root
        activationMode="manual"
        css={{
          display: 'flex',
          width: '100%',
          '&[data-orientation="horizontal"]': {
            flexDirection: 'column',
          },
        }}
        onValueChange={onChange}
        orientation={orientation}
        value={value}
      >
        <TabsPrimitive.List
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          css={[
            {
              display: 'flex',
              '&[data-orientation="vertical"]': {
                borderRight: tabListBorderStyle,
                flexDirection: 'column',
                marginRight: theme.spacing[4],
              },
              '&[data-orientation="horizontal"]': {
                borderBottom: tabListBorderStyle,
                marginBottom: theme.spacing[4],
                maxWidth: '100%',
                overflowX: 'auto',
                [down('sm')]: {
                  scrollbarWidth: 'none',
                  '::-webkit-scrollbar': {
                    display: 'none',
                  },
                },
              },
            },
            align === 'right' &&
              orientation === 'horizontal' && {
                justifyContent: 'end',
              },
            align === 'center' &&
              orientation === 'horizontal' && {
                gap: theme.spacing[6],
                justifyContent: 'center',
              },
          ]}
        >
          {triggers}
        </TabsPrimitive.List>

        {contents}
      </TabsPrimitive.Root>
    </TabsContext.Provider>
  );
}

Tabs.Item = function TabItem({ children }: TabItemProps) {
  const tabsContext = useContext(TabsContext);

  if (!tabsContext) {
    throw new Error('Tabs.Item can only be used within Tabs');
  }

  return <>{children}</>;
};
